import type { TFunction } from 'i18next';

import type { TUnitOfTime } from '@feathr/blackbox';

export interface ITimeUnitOption {
  id: TUnitOfTime;
  value: string;
}

export const secondsPerUnit: Record<TUnitOfTime, number> = {
  weeks: 604800,
  days: 86400,
  hours: 3600,
  minutes: 60,
};

export const minimumDisplayValues: Record<TUnitOfTime, number> = {
  minutes: 5,
  hours: 1,
  days: 1,
  weeks: 1,
};

export function bestUnit(seconds: number): TUnitOfTime {
  if (seconds === 0) {
    return 'minutes';
  }
  const entry = Object.entries(secondsPerUnit).find(([, s]) => seconds % s === 0);
  return (entry?.[0] as TUnitOfTime) ?? 'minutes';
}

export function toDisplayValue(seconds: number, unit: TUnitOfTime): number {
  return Math.round(seconds / secondsPerUnit[unit]);
}

/**
 * Converts a displayed value back to seconds. The value is rounded to a whole number of units, as
 * {@link toDisplayValue} displays it, so a typed fraction is stored as the value the user then sees.
 *
 * @param displayValue - The number of units.
 * @param unit - The unit of time.
 * @returns The equivalent whole number of seconds.
 */
export function toSeconds(displayValue: number, unit: TUnitOfTime): number {
  return Math.round(displayValue) * secondsPerUnit[unit];
}

/**
 * Names a unit of time, pluralized for a count.
 *
 * @param unit - The unit of time.
 * @param count - The number of units, which decides the plural form.
 * @param t - The translation function.
 * @returns The unit's name, e.g. "day" or "days".
 */
export function getUnitLabel(unit: TUnitOfTime, count: number, t: TFunction): string {
  return {
    minutes: t('minute', { count }),
    hours: t('hour', { count }),
    days: t('day', { count }),
    weeks: t('week', { count }),
  }[unit];
}

export function getTimeUnitOptions(): ITimeUnitOption[] {
  return [
    { id: 'minutes', value: 'minutes' },
    { id: 'hours', value: 'hours' },
    { id: 'days', value: 'days' },
    { id: 'weeks', value: 'weeks' },
  ];
}

export function getTimeUnitOptionValue(option: ITimeUnitOption): string {
  return option.value;
}
